import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { IconComponent } from '@uniit/ui-kit';
import { SessionStore } from '../state/session.store';

/**
 * Écran plein cadre affiché tant que l'authentification n'est pas résolue en mode branché
 * (`SessionStore.authStatus` ≠ `ready`). Bâti sur la recette `.uni-splash` du design system : tuile
 * d'app + nom + pastille de statut (spinner). Il évite le clignotement du shell pendant le
 * `GET /api/me`, montre la redirection vers UniPortail sur 401, et un écran d'erreur avec
 * « Réessayer » si l'API est injoignable. En mode démo, l'état vaut `ready` : jamais rendu.
 */
@Component({
  selector: 'uniit-session-gate',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  template: `
    <div class="uni-splash" role="status" aria-live="polite">
      <div class="uni-splash__content">
        <div class="uni-splash__id">
          <img class="app-splash__mark" src="assets/uniit.svg" alt="" width="72" height="72" />
          <span class="uni-splash__sep"></span>
          <span class="uni-splash__name">
            <span class="uni-splash__name-pre">Uni</span><span class="uni-splash__name-suf">IT</span>
          </span>
        </div>

        @switch (session.authStatus()) {
          @case ('error') {
            <div class="uni-splash__status app-splash__status--error">
              <uniit-icon name="circle-alert" [size]="18" />
              <span class="uni-splash__label">Session indisponible — service injoignable.</span>
            </div>
            <button type="button" class="uni-btn uni-btn--primary" (click)="session.retry()">
              Réessayer
            </button>
          }
          @case ('forbidden') {
            <div class="uni-splash__status app-splash__status--error">
              <uniit-icon name="circle-alert" [size]="18" />
              <span class="uni-splash__label">Vous n'êtes pas habilité au module UniIT.</span>
            </div>
            <p class="app-splash__hint">
              Votre compte est authentifié mais n'a pas accès à UniIT. Demandez l'habilitation à un
              administrateur, ou revenez à vos autres modules.
            </p>
            <button type="button" class="uni-btn uni-btn--secondary" (click)="session.backToPortal()">
              Retour à UniPortail
            </button>
          }
          @case ('redirecting') {
            <div class="uni-splash__status">
              <span class="uni-splash__spin" aria-hidden="true"></span>
              <span class="uni-splash__label">Redirection vers UniPortail — connexion Microsoft 365…</span>
            </div>
          }
          @default {
            <div class="uni-splash__status">
              <span class="uni-splash__spin" aria-hidden="true"></span>
              <span class="uni-splash__label">Chargement de votre session…</span>
            </div>
          }
        }
      </div>
    </div>
  `,
  styles: `
    /* Le composant s'appuie sur la recette .uni-splash du kit (fond, séparateur, nom, pastille,
       spinner). Ne restent ici que l'icône d'app réelle (identité acier, jamais redessinée — on sert
       l'asset) et la teinte identité du suffixe du nom. */
    .app-splash__mark {
      display: block;
      width: 72px;
      height: 72px;
    }
    /* Nom : suffixe en acier (identité UniIT) — azure reste réservé à l'action et à UniPortail. */
    .uni-splash__name-suf {
      color: var(--app-uni-it-text-brand);
    }
    /* Variante erreur de la pastille de statut (texte + glyphe en rouge). */
    .app-splash__status--error {
      color: var(--danger);
    }
    /* Sous-texte explicatif de l'état « pas d'accès ». */
    .app-splash__hint {
      margin: 0;
      max-width: 420px;
      font: 400 13px/1.5 var(--font-ui);
      color: var(--fg-2);
    }
  `,
})
export class SessionGateComponent {
  protected readonly session = inject(SessionStore);
}
